import React, { useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { format } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import { CalendarIcon, FilterIcon, XIcon } from 'lucide-react';
import { FeatureCollection, Point } from 'geojson';
import { useApiQuery } from '../../hooks/useApiQuery';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

type HeatmapPoint = {
  latitude: number;
  longitude: number;
  registrationCount: number;
  canvasserHours: number;
  intensity: number;
};

type HeatmapData = {
  totalRegistrations: number;
  totalCanvasserHours: number;
  effectiveness: number;
  uniqueCanvassers: number;
  activeClusters: number;
  points: HeatmapPoint[];
};

type HeatmapResponse = {
  success: boolean;
  message: string;
  data: HeatmapData;
};

type HeatmapFeatureProperties = {
  registrationCount: number;
  canvasserHours: number;
  intensity: number;
  rawIntensity: number;
  spreadWeight: number;
};

type FilterType = 'year' | 'custom' | 'all';

type SelectedLocation = {
  latitude: number;
  longitude: number;
};

const INITIAL_CENTER: [number, number] = [3.4219, 6.4281];
const INITIAL_ZOOM = 12;
const DEFAULT_RADIUS = 3000;

const EMPTY_STATS: Omit<HeatmapData, 'points'> = {
  totalRegistrations: 0,
  totalCanvasserHours: 0,
  effectiveness: 0,
  uniqueCanvassers: 0,
  activeClusters: 0,
};

const buildGeoJsonData = (points: HeatmapPoint[]): FeatureCollection<Point, HeatmapFeatureProperties> => {
  if (points.length === 0) {
    return {
      type: 'FeatureCollection',
      features: [],
    };
  }

  return {
    type: 'FeatureCollection',
    features: points.map((point) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [point.longitude, point.latitude],
      },
      properties: {
        registrationCount: point.registrationCount,
        canvasserHours: point.canvasserHours,
        intensity: point.intensity / 100,
        rawIntensity: point.intensity,
        spreadWeight: Math.max(
          point.intensity / 100,
          point.registrationCount > 0 ? Math.min(point.registrationCount / 10, 1) : 0,
          point.canvasserHours > 0 ? Math.min(point.canvasserHours / 2, 1) : 0.15
        ),
      },
    })),
  };
};

const HeatMap = () => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [appliedRange, setAppliedRange] = useState<{ startDate: string; endDate: string } | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [radiusMeters, setRadiusMeters] = useState(DEFAULT_RADIUS);
  const [selectedLocation, setSelectedLocation] = useState<SelectedLocation | null>(null);

  const requestParams = useMemo(() => {
    const params: Record<string, string | number> = {
    };

    if (selectedLocation) {
      params.latitude = selectedLocation.latitude;
      params.longitude = selectedLocation.longitude;
      params.radiusMeters = radiusMeters;
    }

    if (filterType === 'year') {
      params.period = 'year';
    }

    if (filterType === 'custom' && appliedRange) {
      params.startDate = appliedRange.startDate;
      params.endDate = appliedRange.endDate;
    }

    return Object.keys(params).length > 0 ? params : undefined;
  }, [selectedLocation, radiusMeters, filterType, appliedRange]);

  const { data: initialData, isLoading: initialLoading } = useApiQuery<HeatmapResponse>({
    queryKey: ['partner-heat-map', 'initial'],
    request: {
      method: 'GET',
      url: '/analytics/partner/heat-map',
    },
  });

  const hasActiveRequest = !!requestParams && (filterType !== 'custom' || !!appliedRange);

  const { data: activeData, isLoading: activeLoading } = useApiQuery<HeatmapResponse>({
    queryKey: [
      'partner-heat-map-active',
      filterType,
      selectedLocation?.latitude ?? null,
      selectedLocation?.longitude ?? null,
      radiusMeters,
      appliedRange?.startDate ?? null,
      appliedRange?.endDate ?? null,
    ],
    request: {
      method: 'GET',
      url: '/analytics/partner/heat-map',
      params: requestParams,
    },
    enabled: hasActiveRequest,
  });

  const baseHeatmapData = initialData?.data ?? null;
  const activeHeatmapData = hasActiveRequest ? (activeData?.data ?? null) : null;
  const currentHeatmapData = activeHeatmapData ?? baseHeatmapData;
  const isLoading = initialLoading || activeLoading;
  const basePoints = useMemo(() => baseHeatmapData?.points ?? [], [baseHeatmapData]);
  const activePoints = useMemo(() => activeHeatmapData?.points ?? [], [activeHeatmapData]);
  const displayPoints = activePoints.length > 0 ? activePoints : basePoints;
  const baseGeoJsonData = useMemo(() => buildGeoJsonData(basePoints), [basePoints]);
  const activeGeoJsonData = useMemo(() => buildGeoJsonData(activePoints), [activePoints]);

  const selectedLocationGeoJson: FeatureCollection<Point> = useMemo(() => {
    if (!selectedLocation) {
      return {
        type: 'FeatureCollection',
        features: [],
      };
    }

    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [selectedLocation.longitude, selectedLocation.latitude],
          },
          properties: {},
        },
      ],
    };
  }, [selectedLocation]);

  useEffect(() => {
    if (!mapContainerRef.current) {
      return;
    }

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: INITIAL_CENTER,
      zoom: INITIAL_ZOOM,
      attributionControl: false,
    });

    map.addControl(new mapboxgl.AttributionControl(), 'bottom-right');
    mapRef.current = map;

    const handleMapClick = (event: mapboxgl.MapMouseEvent) => {
      const nextLocation = {
        latitude: Number(event.lngLat.lat.toFixed(6)),
        longitude: Number(event.lngLat.lng.toFixed(6)),
      };

      setSelectedLocation(nextLocation);
      map.easeTo({
        center: [nextLocation.longitude, nextLocation.latitude],
        duration: 700,
      });
    };

    map.on('load', () => {
      map.getCanvas().style.cursor = 'crosshair';
    });
    map.on('click', handleMapClick);

    return () => {
      map.off('click', handleMapClick);
      map.remove();
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    const map = mapRef.current;

    const initializeLayers = () => {
      if (!map.getSource('base-heatmap-source')) {
        map.addSource('base-heatmap-source', {
          type: 'geojson',
          data: baseGeoJsonData,
        });

        map.addLayer({
          id: 'base-heatmap-layer',
          type: 'heatmap',
          source: 'base-heatmap-source',
          maxzoom: 15,
          paint: {
            'heatmap-weight': [
              'interpolate',
              ['linear'],
              ['get', 'spreadWeight'],
              0, 0.15,
              0.2, 0.35,
              0.5, 0.7,
              1, 1,
            ],
            'heatmap-intensity': [
              'interpolate',
              ['linear'],
              ['zoom'],
              0, 1.2,
              9, 1.8,
              15, 3.4,
            ],
            'heatmap-color': [
              'interpolate',
              ['linear'],
              ['heatmap-density'],
              0, 'rgba(255,255,255,0)',
              0.2, 'rgba(219,234,254,0.4)',
              0.4, 'rgba(191,219,254,0.72)',
              0.6, 'rgba(96,165,250,0.78)',
              0.8, 'rgba(37,99,235,0.82)',
              1, 'rgba(30,64,175,0.86)',
            ],
            'heatmap-radius': [
              'interpolate',
              ['linear'],
              ['zoom'],
              0, 32,
              8, 48,
              15, 72,
            ],
          },
        });
      } else {
        (map.getSource('base-heatmap-source') as mapboxgl.GeoJSONSource).setData(baseGeoJsonData);
      }

      if (!map.getSource('heatmap-source')) {
        map.addSource('heatmap-source', {
          type: 'geojson',
          data: activeGeoJsonData,
        });

        map.addLayer({
          id: 'heatmap-layer',
          type: 'heatmap',
          source: 'heatmap-source',
          maxzoom: 15,
          paint: {
            'heatmap-weight': [
              'interpolate',
              ['linear'],
              ['get', 'spreadWeight'],
              0, 0.2,
              0.2, 0.45,
              0.5, 0.8,
              1, 1,
            ],
            'heatmap-intensity': [
              'interpolate',
              ['linear'],
              ['zoom'],
              0, 1.4,
              9, 2.2,
              15, 3.8,
            ],
            'heatmap-color': [
              'interpolate',
              ['linear'],
              ['heatmap-density'],
              0, 'rgba(255,255,255,0)',
              0.2, 'rgba(219,234,254,0.6)',
              0.4, 'rgb(191,219,254)',
              0.6, 'rgb(96,165,250)',
              0.8, 'rgb(37,99,235)',
              1, 'rgb(30,64,175)',
            ],
            'heatmap-radius': [
              'interpolate',
              ['linear'],
              ['zoom'],
              0, 36,
              8, 54,
              15, 80,
            ],
            'heatmap-opacity': 0.9,
          },
        });

        map.addLayer({
          id: 'heatmap-points-layer',
          type: 'circle',
          source: 'heatmap-source',
          paint: {
            'circle-radius': [
              'interpolate',
              ['linear'],
              ['get', 'registrationCount'],
              0, 7,
              10, 15,
              50, 22,
            ],
            'circle-color': '#0f172a',
            'circle-opacity': 0.32,
            'circle-stroke-width': 1.5,
            'circle-stroke-color': '#ffffff',
          },
        });
      } else {
        (map.getSource('heatmap-source') as mapboxgl.GeoJSONSource).setData(activeGeoJsonData);
      }

      if (!map.getSource('selected-location-source')) {
        map.addSource('selected-location-source', {
          type: 'geojson',
          data: selectedLocationGeoJson,
        });

        map.addLayer({
          id: 'selected-location-layer',
          type: 'circle',
          source: 'selected-location-source',
          paint: {
            'circle-radius': 9,
            'circle-color': '#ffffff',
            'circle-stroke-width': 3,
            'circle-stroke-color': '#0ea5e9',
          },
        });
      } else {
        (map.getSource('selected-location-source') as mapboxgl.GeoJSONSource).setData(selectedLocationGeoJson);
      }
    };

    if (map.isStyleLoaded()) {
      initializeLayers();
    } else {
      map.once('load', initializeLayers);
    }
    if (map.getLayer('heatmap-layer')) {
      map.setLayoutProperty('heatmap-layer', 'visibility', activePoints.length > 0 ? 'visible' : 'none');
    }
    if (map.getLayer('heatmap-points-layer')) {
      map.setLayoutProperty('heatmap-points-layer', 'visibility', activePoints.length > 0 ? 'visible' : 'none');
    }
  }, [baseGeoJsonData, activeGeoJsonData, selectedLocationGeoJson, activePoints.length]);

  useEffect(() => {
    if (!mapRef.current || displayPoints.length === 0) {
      return;
    }

    const bounds = new mapboxgl.LngLatBounds();
    displayPoints.forEach((point) => {
      bounds.extend([point.longitude, point.latitude]);
    });

    if (!bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds, {
        padding: 120,
        duration: 900,
        maxZoom: 15,
      });
    }
  }, [displayPoints]);

  const handleFilterChange = (type: FilterType) => {
    setFilterType(type);

    if (type !== 'custom') {
      setStartDate(null);
      setEndDate(null);
      setAppliedRange(null);
      setShowDatePicker(false);
    }
  };

  const handleDateRangeSubmit = () => {
    if (!startDate || !endDate) {
      return;
    }

    setAppliedRange({
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
    });
    setShowDatePicker(false);
  };

  const stats = useMemo(() => {
    if (!currentHeatmapData) {
      return EMPTY_STATS;
    }

    return {
      totalRegistrations: currentHeatmapData.totalRegistrations,
      totalCanvasserHours: currentHeatmapData.totalCanvasserHours,
      effectiveness: currentHeatmapData.effectiveness,
      uniqueCanvassers: currentHeatmapData.uniqueCanvassers,
      activeClusters: currentHeatmapData.activeClusters,
    };
  }, [currentHeatmapData]);

  const headerDescription = useMemo(() => {
    if (!selectedLocation) {
      return 'The map loads with partner-wide heat map data first. Click any point to drill into registrations, canvasser hours, and cluster activity around that location.';
    }

    if (filterType === 'year') {
      return 'Year-to-date performance around the selected location.';
    }

    if (filterType === 'custom' && appliedRange) {
      return `Custom range from ${format(new Date(appliedRange.startDate), 'MMM dd, yyyy')} to ${format(new Date(appliedRange.endDate), 'MMM dd, yyyy')}.`;
    }

    if (filterType === 'custom') {
      return 'Select and apply a custom date range to run the location analysis.';
    }

    return 'All-time performance around the selected location.';
  }, [selectedLocation, filterType, appliedRange]);

  return (
    <div className="relative h-[100vh] w-full overflow-hidden rounded-lg bg-white">
      <div className="absolute left-4 right-4 top-4 z-10">
        <div className="overflow-visible rounded-[30px] border border-white/80 p-4 shadow-[0_24px_80px_rgba(15,23,42,0.16)] backdrop-blur-sm md:p-5">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-3xl">
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-sky-100 bg-sky-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  Click-To-Inspect
                </div>
                <h2 className="text-xl font-semibold tracking-tight text-slate-900 md:text-[1.5rem]">
                  Partner Activity Heat Map
                </h2>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500 md:text-[15px]">
                  {headerDescription}
                </p>
                {selectedLocation && (
                  <div className="mt-3 inline-flex flex-wrap items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600">
                    <span>Lat {selectedLocation.latitude.toFixed(4)}</span>
                    <span className="text-slate-300">/</span>
                    <span>Lng {selectedLocation.longitude.toFixed(4)}</span>
                    <span className="text-slate-300">/</span>
                    <span>Radius {radiusMeters.toLocaleString()}m</span>
                  </div>
                )}
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center justify-center gap-2 self-start rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-[0_10px_30px_rgba(15,23,42,0.08)] transition-all hover:-translate-y-0.5 hover:border-sky-200 hover:text-slate-900"
              >
                <FilterIcon className="h-4 w-4 text-sky-600" />
                {showFilters ? 'Hide Filters' : 'Open Filters'}
              </button>
            </div>

            {showFilters && (
              <div className="rounded-[26px] border border-slate-200/80 bg-gradient-to-br from-slate-50 via-white to-sky-50 p-3 shadow-inner shadow-slate-100/70 md:p-4">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => handleFilterChange('year')}
                        className={`rounded-full px-4 py-2.5 text-sm font-medium transition-all ${filterType === 'year'
                          ? 'bg-slate-900 text-white shadow-[0_16px_32px_rgba(15,23,42,0.18)]'
                          : 'border border-slate-200 bg-white text-slate-600 hover:border-sky-200 hover:text-slate-900'
                          }`}
                      >
                        This Year
                      </button>
                      <button
                        onClick={() => handleFilterChange('custom')}
                        className={`rounded-full px-4 py-2.5 text-sm font-medium transition-all ${filterType === 'custom'
                          ? 'bg-slate-900 text-white shadow-[0_16px_32px_rgba(15,23,42,0.18)]'
                          : 'border border-slate-200 bg-white text-slate-600 hover:border-sky-200 hover:text-slate-900'
                          }`}
                      >
                        Custom Range
                      </button>
                      <button
                        onClick={() => handleFilterChange('all')}
                        className={`rounded-full px-4 py-2.5 text-sm font-medium transition-all ${filterType === 'all'
                          ? 'bg-slate-900 text-white shadow-[0_16px_32px_rgba(15,23,42,0.18)]'
                          : 'border border-slate-200 bg-white text-slate-600 hover:border-sky-200 hover:text-slate-900'
                          }`}
                      >
                        All Time
                      </button>
                    </div>

                    <div className="min-w-0 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm xl:min-w-[320px]">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Radius</p>
                          <p className="mt-1 text-sm font-medium text-slate-700">{radiusMeters.toLocaleString()} meters</p>
                        </div>
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">10m - 100,000m</span>
                      </div>
                      <input
                        type="range"
                        min={10}
                        max={100000}
                        step={10}
                        value={radiusMeters}
                        onChange={(event) => setRadiusMeters(Number(event.target.value))}
                        className="mt-4 h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-sky-600"
                      />
                    </div>
                  </div>

                  {filterType === 'custom' && (
                    <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center">
                      <button
                        onClick={() => setShowDatePicker(!showDatePicker)}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-[0_12px_24px_rgba(15,23,42,0.06)] transition-all hover:border-sky-200 hover:text-slate-900"
                      >
                        <CalendarIcon className="h-4 w-4 text-sky-600" />
                        {appliedRange && startDate && endDate
                          ? `${format(startDate, 'MMM dd')} - ${format(endDate, 'MMM dd')}`
                          : 'Select Date Range'
                        }
                      </button>

                      <p className="text-sm text-slate-500">
                        Apply a specific date window for the selected map location.
                      </p>

                      {showDatePicker && (
                        <div className="absolute left-0 top-full z-20 mt-2 w-full max-w-md rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_30px_60px_rgba(15,23,42,0.18)]">
                          <div className="mb-4 flex items-center justify-between">
                            <div>
                              <h3 className="text-base font-semibold text-slate-900">Select Date Range</h3>
                              <p className="text-sm text-slate-500">Choose the window to run the map analysis.</p>
                            </div>
                            <button
                              onClick={() => setShowDatePicker(false)}
                              className="rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                            >
                              <XIcon className="h-4 w-4" />
                            </button>
                          </div>

                          <div className="space-y-3">
                            <div>
                              <label className="mb-1 block text-sm font-medium text-slate-700">Start Date</label>
                              <DatePicker
                                selected={startDate}
                                onChange={(date: Date | null) => setStartDate(date)}
                                selectsStart
                                startDate={startDate}
                                endDate={endDate}
                                maxDate={endDate || new Date()}
                                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-sky-300 focus:bg-white"
                              />
                            </div>

                            <div>
                              <label className="mb-1 block text-sm font-medium text-slate-700">End Date</label>
                              <DatePicker
                                selected={endDate}
                                onChange={(date: Date | null) => setEndDate(date)}
                                selectsEnd
                                startDate={startDate}
                                endDate={endDate}
                                minDate={startDate || undefined}
                                maxDate={new Date()}
                                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-sky-300 focus:bg-white"
                              />
                            </div>

                            <button
                              onClick={handleDateRangeSubmit}
                              disabled={!startDate || !endDate}
                              className="w-full rounded-2xl bg-slate-900 px-4 py-3 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                            >
                              Apply Range
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
              <div className="rounded-[24px] border border-slate-100 bg-gradient-to-br from-white to-sky-50 p-4 shadow-[0_12px_30px_rgba(148,163,184,0.14)]">
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Total Registrations</div>
                <div className="mt-3 text-2xl font-semibold tracking-tight text-slate-900">
                  {stats.totalRegistrations.toLocaleString()}
                </div>
              </div>
              
              <div className="rounded-[24px] border border-slate-100 bg-gradient-to-br from-white to-emerald-50 p-4 shadow-[0_12px_30px_rgba(148,163,184,0.14)]">
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Effectiveness</div>
                <div className="mt-3 text-2xl font-semibold tracking-tight text-slate-900">
                  {stats.effectiveness ? `${stats.effectiveness?.toFixed(0)}` : "--"} <span className="text-sm text-gray-500">reg. per hr</span>
                </div>
              </div>
              
              <div className="rounded-[24px] border border-slate-100 bg-gradient-to-br from-white to-rose-50 p-4 shadow-[0_12px_30px_rgba(148,163,184,0.14)]">
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Active Clusters</div>
                <div className="mt-3 text-2xl font-semibold tracking-tight text-slate-900">
                  {stats.activeClusters.toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-900/20">
          <div className="rounded-2xl bg-white/90 px-6 py-5 shadow-xl backdrop-blur-sm">
            <div className="text-center">
              <div className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-b-2 border-sky-600" />
              <p className="text-sm font-medium text-slate-700">Loading heat map data...</p>
            </div>
          </div>
        </div>
      )}

      {!selectedLocation && (
        <div className="pointer-events-none absolute inset-x-0 bottom-24 z-10 mx-auto flex w-fit items-center gap-2 rounded-full border border-white/80 bg-white/90 px-4 py-2 text-sm font-medium text-slate-600 shadow-lg backdrop-blur-sm">
          Showing the saved overall heat map. Click a map location to overlay a refined analysis.
        </div>
      )}

      <div ref={mapContainerRef} className="h-full w-full" />

      <div className="absolute bottom-4 right-4 z-10 rounded-lg bg-neutral-500 p-3 backdrop-blur-sm">
        <div className="mb-2 text-xs text-gray-800">Intensity</div>
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 rounded" style={{ backgroundColor: 'rgba(255, 255, 255, 1)' }} />
          <div className="h-4 w-4 rounded" style={{ backgroundColor: 'rgba(219, 234, 254, 1)' }} />
          <div className="h-4 w-4 rounded" style={{ backgroundColor: 'rgba(191, 219, 254, 1)' }} />
          <div className="h-4 w-4 rounded" style={{ backgroundColor: 'rgba(96, 165, 250, 1)' }} />
          <div className="h-4 w-4 rounded" style={{ backgroundColor: 'rgba(37, 99, 235, 1)' }} />
          <div className="h-4 w-4 rounded" style={{ backgroundColor: 'rgba(30, 64, 175, 1)' }} />
        </div>
        <div className="mt-1 flex justify-between text-xs text-gray-800">
          <span>Low</span>
          <span>High</span>
        </div>
      </div>
    </div>
  );
};

export default HeatMap;
